import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

import { AuthState } from './auth.store';
import { AsyncState } from '../../app/models/AsyncState';
import { AsyncStatus } from '../../app/enums/AsyncStatus';
import { IProfileModel } from '../models/Auth';

export const selectSelf = (state: RootState): RootState => state;

export const selectAuthState = createSelector(
  selectSelf,
  (state: RootState): AuthState => state.auth
);

export const selectLoginState = createSelector(
  selectAuthState,
  (auth: AuthState): AsyncState<null> => auth.login
);

export const selectLoginStatus = createSelector(
  selectLoginState,
  (login: AsyncState<null>): AsyncStatus => login.status
);

export const selectIsLoginStatusPending = createSelector(
  selectLoginStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Pending
);

export const selectRegisterState = createSelector(
  selectAuthState,
  (auth: AuthState): AsyncState<null> => auth.register
);

export const selectRegisterStatus = createSelector(
  selectRegisterState,
  (register: AsyncState<null>): AsyncStatus => register.status
);

export const selectIsRegisterStatusPending = createSelector(
  selectRegisterStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Pending
);

export const selectResetPasswordState = createSelector(
  selectAuthState,
  (auth: AuthState): AsyncState<null> => auth.resetPassword
);

export const selectResetPasswordStatus = createSelector(
  selectResetPasswordState,
  (resetPassword: AsyncState<null>): AsyncStatus => resetPassword.status
);

export const selectIsResetPasswordStatusPending = createSelector(
  selectResetPasswordStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Pending
);

export const selectRecoveryPasswordState = createSelector(
  selectAuthState,
  (auth: AuthState): AsyncState<null> => auth.recoverPassword
);

export const selectRecoveryPasswordStatus = createSelector(
  selectRecoveryPasswordState,
  (recoveryPassword: AsyncState<null>): AsyncStatus => recoveryPassword.status
);

export const selectIsRecoveryPasswordStatusPending = createSelector(
  selectRecoveryPasswordStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Pending
);

export const selectAuthorizationState = createSelector(
  selectAuthState,
  (auth: AuthState): AsyncState<boolean> => auth.authorization
);

export const selectAuthorizationData = createSelector(
  selectAuthorizationState,
  (authorization: AsyncState<boolean>): boolean => authorization.data || false
);

export const selectAuthorizationStatus = createSelector(
  selectAuthorizationState,
  (data: AsyncState<boolean>): AsyncStatus => data.status
);

export const selectIsAuthorizationVoid = createSelector(
  selectAuthorizationStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Void
);

export const selectIsAuthorizationPending = createSelector(
  selectAuthorizationStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Pending
);

export const selectIsAuthorizationFulfilled = createSelector(
  selectAuthorizationStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Fulfilled
);

export const selectIsAuthorizationRejected = createSelector(
  selectAuthorizationStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Rejected
);
export const selectProfileState = createSelector(
  selectAuthState,
  (auth: AuthState): AsyncState<IProfileModel | null> => auth.profile
);

export const selectProfileData = createSelector(
  selectProfileState,
  (profile: AsyncState<IProfileModel | null>): IProfileModel | null =>
    profile.data || null
);
export const selectChangePasswordState = createSelector(
  selectAuthState,
  (auth: AuthState): AsyncState<null> => auth.changePassword
);

export const selectChangePasswordStatus = createSelector(
  selectChangePasswordState,
  (changePassword: AsyncState<null>): AsyncStatus => changePassword.status
);
export const selectIsChangePasswordStatusPending = createSelector(
  selectChangePasswordStatus,
  (status: AsyncStatus): boolean => status === AsyncStatus.Pending
);
